import React, { isValidElement, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Paper, TableContainer, TableHead, TableRow, Table, TablePagination, TableCell, TableBody, Icon, Card, CardContent, Tooltip, IconButton, TableSortLabel, Box, Grid, Typography, CircularProgress, Popover, Checkbox, TextField, InputAdornment, Divider, Button, Menu, MenuItem } from '@mui/material'
import { isNil, isNumber, isObject, isString, isUndefined, orderBy, partition, sum, toString, uniq } from 'lodash'
import { useLocation } from 'react-router'
import { styled } from '@mui/styles'
import { CardTitle } from 'common/components/card/Card'
import CloudOffIcon from '@mui/icons-material/CloudOff'
import SyncProblemIcon from '@mui/icons-material/SyncProblem'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import FilterAltIcon from '@mui/icons-material/FilterAlt'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import SearchIcon from '@mui/icons-material/Search'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import useUpdateEffect from 'common/hooks/useUpdateEffect'
import { getTextColorByHexColor } from 'common/utils/ColorUtil'
import { STATUS } from 'common/constants/storeConstants'

// https://virtuoso.dev/mui-table-virtual-scroll/

const SORT = {
    ASC: 'asc',
    DESC: 'desc',
}

const ALIGN = {
    LEFT: 'left',
    RIGHT: 'right',
    CENTER: 'center',
    TOP: 'top',
    BOTTOM: 'bottom',
    SPACE_BETWEEN: 'space-between',
}

// Persistance de l'état d'un tableau (tri, page, nb d'éléments, filtres colonne) dans le localStorage,
// même encodage que le hook useLocalStorage maison. Opt-in via la prop `rememberState`.
const getStorageValue = (key) => {
    try {
        const raw = window.localStorage.getItem(key)
        return raw !== null ? JSON.parse(decodeURIComponent(window.atob(raw))) : undefined
    } catch {
        return undefined
    }
}
const setStorageValue = (key, value) => {
    try {
        window.localStorage.setItem(key, window.btoa(encodeURIComponent(JSON.stringify(value))))
    } catch {
        // stockage indisponible : on ignore silencieusement
    }
}

const isDefined = v => !((isNumber(v) && isNaN(v)) || isNil(v) || v === '') || v === 0
const sortRows = (rows, sort = {}) => {
    const { column, direction } = sort
    if (isUndefined(column) || isUndefined(direction)) return rows

    const [withValue, withoutValue] = partition(rows, elem => {
        if (isValidElement(elem[column])) return false
        if (!isObject(elem[column])) return isDefined(elem[column])
        const {
            value,
            sortValue = value,
        } = elem[column]
        if (isValidElement(sortValue)) return false
        return isDefined(sortValue)
    })
    const getSortValue = elem => {
        const v = isObject(elem[column]) ? elem[column]?.sortValue ?? elem[column]?.value : elem[column]
        // Tri insensible à la casse pour les chaînes (sinon les majuscules passent avant les minuscules)
        return isString(v) ? v.toLowerCase() : v
    }

    const orderedRows = orderBy(withValue, getSortValue, direction)
    return [...orderedRows, ...withoutValue]
}

// Valeur utilisée pour regrouper/filtrer une cellule : la valeur affichée (value),
// fallback sortValue. Les éléments React et les valeurs vides retournent null
// (regroupés sous le libellé « Vide »).
const getFilterKey = cell => {
    if (isNil(cell) || cell === '' || isValidElement(cell)) return null
    if (!isObject(cell)) return cell
    const { value, sortValue } = cell
    const displayed = !isValidElement(value) && isDefined(value) ? value : sortValue
    if (isValidElement(displayed) || isNil(displayed) || displayed === '') return null
    return displayed
}

const applyColumnFilters = (rows, filters) => {
    const columns = Object.keys(filters)
    if (!columns.length) return rows
    return rows.filter(row => columns.every(key => filters[key].includes(getFilterKey(row[key]))))
}

const getOptionLabel = value => (isNil(value) || value === '' ? 'Vide' : toString(value))

// Fond des lignes impaires (zébrage très léger) pour faciliter la lecture des tableaux.
const ZEBRA_COLOR = '#f7f7f7'

const FILTER_OPTIONS_PER_PAGE = 10

// Popover de filtre d'une colonne (façon IntelliJ) : recherche, tout sélectionner,
// liste des valeurs distinctes avec compteur d'occurrences et cases à cocher.
const ColumnFilterPopover = ({ anchorEl, columnLabel, options, selected, onChange, onClose }) => {
    const [search, setSearch] = useState('')
    const [page, setPage] = useState(0)

    const visibleOptions = useMemo(() => {
        const term = search.trim().toLowerCase()
        if (!term) return options
        return options.filter(o => getOptionLabel(o.value).toLowerCase().includes(term))
    }, [options, search])

    // Revenir à la première page quand la liste affichée change (recherche ou colonne)
    useUpdateEffect(() => {
        setPage(0)
    }, [search, options])

    const pageCount = Math.ceil(visibleOptions.length / FILTER_OPTIONS_PER_PAGE)
    const pagedOptions = visibleOptions.slice(page * FILTER_OPTIONS_PER_PAGE, page * FILTER_OPTIONS_PER_PAGE + FILTER_OPTIONS_PER_PAGE)

    const allVisibleSelected = visibleOptions.length > 0 && visibleOptions.every(o => selected.includes(o.value))
    const someVisibleSelected = visibleOptions.some(o => selected.includes(o.value))

    const toggleValue = value => {
        const next = selected.includes(value) ? selected.filter(v => v !== value) : [...selected, value]
        onChange(next)
    }

    const toggleAllVisible = () => {
        const visibleValues = visibleOptions.map(o => o.value)
        if (allVisibleSelected) {
            onChange(selected.filter(v => !visibleValues.includes(v)))
        } else {
            onChange(uniq([...selected, ...visibleValues]))
        }
    }

    return (
        <Popover
            open={!isNil(anchorEl)}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
            <Box sx={{ width: 320, p: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography sx={{ fontWeight: 'bold' }} noWrap title={columnLabel}>{`Filtre : ${columnLabel}`}</Typography>
                    {selected.length > 0 && (
                        <Button size='small' onClick={() => onChange([])}>Réinitialiser</Button>
                    )}
                </Box>
                <TextField
                    fullWidth
                    size='small'
                    autoFocus
                    placeholder='Rechercher'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    slotProps={{ input: { startAdornment: <InputAdornment position='start'><SearchIcon fontSize='small' /></InputAdornment> } }}
                />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, pt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Checkbox
                            size='small'
                            checked={allVisibleSelected}
                            indeterminate={!allVisibleSelected && someVisibleSelected}
                            onChange={toggleAllVisible}
                        />
                        <Typography variant='caption' color='text.secondary'>Valeur</Typography>
                    </Box>
                    <Typography variant='caption' color='text.secondary'>Nombre</Typography>
                </Box>
                <Divider />
                <Box>
                    {pagedOptions.map((o, i) => (
                        <Box
                            key={i}
                            onClick={() => toggleValue(o.value)}
                            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', pr: 1, '&:hover': { backgroundColor: 'action.hover' } }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                <Checkbox size='small' checked={selected.includes(o.value)} />
                                <Typography variant='body2' noWrap title={getOptionLabel(o.value)}>{getOptionLabel(o.value)}</Typography>
                            </Box>
                            <Typography variant='body2' color='text.secondary' sx={{ pl: 1 }}>{o.count}</Typography>
                        </Box>
                    ))}
                    {!visibleOptions.length && (
                        <Typography variant='body2' color='text.secondary' sx={{ p: 1 }}>Aucun</Typography>
                    )}
                </Box>
                {pageCount > 1 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', pt: 1 }}>
                        <IconButton size='small' disabled={page === 0} onClick={() => setPage(p => p - 1)}>
                            <ChevronLeftIcon fontSize='small' />
                        </IconButton>
                        <Typography variant='body2' sx={{ minWidth: 70, textAlign: 'center' }}>{`${page + 1} / ${pageCount}`}</Typography>
                        <IconButton size='small' disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>
                            <ChevronRightIcon fontSize='small' />
                        </IconButton>
                    </Box>
                )}
            </Box>
        </Popover>
    )
}

ColumnFilterPopover.propTypes = {
    anchorEl: PropTypes.object,
    columnLabel: PropTypes.string,
    options: PropTypes.arrayOf(PropTypes.shape({
        value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        count: PropTypes.number,
    })),
    selected: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
    onChange: PropTypes.func,
    onClose: PropTypes.func,
}

const WrapperBox = styled(Box)({
    padding: '0px 10px',
})

const WrapperPaper = styled(Paper)({
    padding: '0px 10px',
})

const WrapperCardContent = styled(CardContent)({
    overflow: 'hidden',
    padding: '0px 10px',
    ['&:last-child']: {
        paddingBottom: '0',
    },
})

const WrapperAccordionDetails = styled(Paper)({
    padding: '0px 10px',
})

const NewTable = ({
    rows = [],
    headers = [],
    lineActions = [],
    rowsPerPageDefault = 25,

    onClickRow,
    defaultSort = {},

    status,
    maxHeight,
    WrapperComponent = WrapperPaper,
    hideLines,
    filterable = true,
    contextActions,
    rememberState = false,
    rememberAll = false,
}) => {
    // Clé de persistance : route + colonnes du tableau (opt-in via rememberState).
    const location = useLocation()
    const tableKey = useMemo(
        () => window.btoa(encodeURIComponent(`${location.pathname}-${headers.map(h => (isObject(h) ? h.key : h)).join(',')}`)),
        [location.pathname, headers],
    )
    const [storedState] = useState(() => (rememberState ? getStorageValue(tableKey) : undefined))

    const [sort, setSort] = useState(storedState?.sort ?? defaultSort)

    // Menu contextuel (clic droit) par ligne : { mouseX, mouseY, row }
    const [rowMenu, setRowMenu] = useState(null)
    const rowMenuActions = (rowMenu && contextActions) ? contextActions(rowMenu.row) : []

    // Filtrage local par colonne : { [headerKey]: [valeurs sélectionnées] }
    const [columnFilters, setColumnFilters] = useState(storedState?.columnFilters ?? {})
    const [filterAnchor, setFilterAnchor] = useState(null) // { el, columnKey, columnLabel }

    const columnFilteredRows = useMemo(() => applyColumnFilters(rows, columnFilters), [rows, columnFilters])
    const sortedRows = useMemo(() => sortRows(columnFilteredRows, sort), [columnFilteredRows, sort])

    // Valeurs distinctes + nombre d'occurrences de la colonne dont le popover est ouvert
    const activeColumnKey = filterAnchor?.columnKey
    const activeColumnOptions = useMemo(() => {
        if (isNil(activeColumnKey)) return []
        const counts = new Map()
        rows.forEach(row => {
            const value = getFilterKey(row[activeColumnKey])
            counts.set(value, (counts.get(value) ?? 0) + 1)
        })
        return orderBy([...counts.entries()].map(([value, count]) => ({ value, count })),
            o => (isNil(o.value) ? '' : toString(o.value).toLowerCase()))
    }, [rows, activeColumnKey])

    const setColumnFilter = (columnKey, values) => {
        setColumnFilters(prev => {
            const next = { ...prev }
            if (!values.length) {
                Reflect.deleteProperty(next, columnKey)
            } else {
                next[columnKey] = values
            }
            return next
        })
    }

    const [page, setPage] = useState(storedState?.page ?? 0)
    const [rowsPerPage, setRowsPerPage] = useState(storedState?.rowsPerPage ?? rowsPerPageDefault)

    // La page peut être hors bornes si le jeu de lignes rétrécit (recherche parente, filtre) alors
    // qu'une page > 0 était mémorisée -> tableau vide. On borne à la dernière page valide.
    const lastPage = rowsPerPage > 0 ? Math.max(0, Math.ceil(sortedRows.length / rowsPerPage) - 1) : 0
    const safePage = Math.min(page, lastPage)
    useUpdateEffect(() => {
        if (page !== safePage) {
            setPage(safePage)
        }
    }, [safePage])

    // Revenir à la première page dès qu'un filtre de colonne change
    useUpdateEffect(() => {
        setPage(0)
    }, [columnFilters])

    // Persistance de l'état (tri, page, nb d'éléments, filtres colonne) quand rememberState est actif.
    // Par défaut on NE mémorise PAS « Tout » (rowsPerPage === -1) par sécurité mémoire ; opt-in via rememberAll.
    useUpdateEffect(() => {
        if (!rememberState || (rowsPerPage === -1 && !rememberAll)) {
            return
        }
        setStorageValue(tableKey, { sort, page, rowsPerPage, columnFilters })
    }, [sort, page, rowsPerPage, columnFilters])

    const filteredRows = rowsPerPage > 0 ? sortedRows.slice(
        safePage * rowsPerPage,
        safePage * rowsPerPage + rowsPerPage,
    ) : sortedRows

    const filteredLineActions = lineActions.filter(a => a.displayed || isUndefined(a.displayed))
    const [rightActions, leftActions] = partition(filteredLineActions, a => a.align === ALIGN.RIGHT)

    const offsetLeftAction = leftActions.length * 23
    const offsetRightAction = rightActions.length * 23

    const listLeftSize = headers.map(h => h.sticky === ALIGN.LEFT && h.width || 0)
    const listRightSize = headers.map(h => h.sticky === ALIGN.RIGHT && h.width || 0).toReversed()

    const offsetLeftStickyHeaders = listLeftSize.reduce((acc, width, i) => {
        acc.push(offsetLeftAction + sum(listLeftSize.slice(0, i)) + width)
        return acc
    }, [offsetLeftAction])

    const offsetRightStickyHeaders = listRightSize.reduce((acc, width, i) => {
        acc.push(offsetRightAction + sum(listRightSize.slice(0, i)) + width)
        return acc
    }, [offsetRightAction]).toReversed()


    return (
        <WrapperComponent>
            <TableContainer
                sx={{
                    maxHeight,
                    overflowY: maxHeight ? 'auto' : 'hidden',

                    width: 0,
                    minWidth: '100%',
                    overflowX: 'auto',
                }}
            >
                <Table stickyHeader aria-label='sticky table' size='small' sx={{ ['& tr:last-child td']: { borderBottom: columnFilteredRows.length > 10 ? undefined : 'none' } }}>
                    {!hideLines && (
                        <>
                            <TableHead>
                                <TableRow>
                                    {leftActions.map((a, i) => (<TableCell key={lineActions.indexOf(a)} variant='headerIcon' sx={{ left: `${i * 23}px` }} />))}
                                    {headers.map((header, i) => {
                                        const {
                                            key,
                                            value,
                                            onClick,
                                            sticky,
                                            width,
                                            color,
                                            tooltip,
                                            filterable: headerFilterable,
                                        } = isObject(header) ? header : { key: header }
                                        const isNullValue = key.startsWith('nullValue')
                                        const isSorted = sort.column === key
                                        // Le tableau active le filtrage globalement ; chaque colonne est filtrable
                                        // par défaut, le header peut l'exclure via filterable: false
                                        const resolvedFilterable = filterable && (headerFilterable ?? true) && !isNullValue
                                        const isColumnFiltered = !!columnFilters[key]?.length
                                        const headerLabel = isString(value) ? value : key
                                        const sortLabel = !isNullValue && (
                                            <TableSortLabel
                                                active={isSorted}
                                                direction={isSorted ? sort?.direction : undefined}
                                                hideSortIcon
                                                onClick={e => {
                                                    e.stopPropagation()
                                                    if (onClick) {
                                                        onClick(e)
                                                    } else {
                                                        setSort(({ direction, column }) => {
                                                            if (column !== key) return { direction: SORT.ASC, column: key }
                                                            if (direction === SORT.ASC) return { direction: SORT.DESC, column: key }
                                                            return {} // direction === SORT.DESC
                                                        })
                                                    }
                                                }}
                                                sx={{ whiteSpace: 'pre-wrap' }}
                                            >
                                                {value ?? key}
                                            </TableSortLabel>
                                        )
                                        const tableCell = (
                                            <TableCell
                                                variant='head'
                                                sx={{
                                                    backgroundColor: color,
                                                    minWidth: width,
                                                    ...(!isNil(sticky) ? {
                                                        position: 'sticky',
                                                        zIndex: 3,
                                                        [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                                    } : {}),
                                                }}
                                                key={i}
                                                sortDirection={isSorted ? sort?.direction : false}
                                            >
                                                {resolvedFilterable ? (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 0.25, '& .MuiTableSortLabel-root': { width: 'auto', paddingRight: '4px' } }}>
                                                        {sortLabel}
                                                        <IconButton
                                                            size='small'
                                                            sx={{ p: 0.25, color: getTextColorByHexColor(color) }}
                                                            onClick={e => {
                                                                e.stopPropagation()
                                                                setFilterAnchor({ el: e.currentTarget, columnKey: key, columnLabel: headerLabel })
                                                            }}
                                                        >
                                                            {isColumnFiltered
                                                                ? <FilterAltIcon fontSize='small' />
                                                                : <FilterAltOutlinedIcon fontSize='small' />}
                                                        </IconButton>
                                                    </Box>
                                                ) : sortLabel}
                                            </TableCell>
                                        )
                                        if (tooltip) {
                                            return (
                                                <Tooltip key={i} title={tooltip}>
                                                    {tableCell}
                                                </Tooltip>
                                            )
                                        }
                                        return tableCell
                                    })}
                                    {rightActions.map((a, i) => (<TableCell key={lineActions.indexOf(a)} variant='headerIcon' sx={{ left: `${i * 23}px` }} />))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {status !== STATUS.LOADING && filteredRows.map((row, displayIndex) => {
                                    const ir = rows.indexOf(row)
                                    // Lignes alternées (zébrage léger) pour mieux suivre les lignes ; colorLine reste prioritaire.
                                    const lineColor = row.colorLine ?? (displayIndex % 2 === 1 ? ZEBRA_COLOR : 'white')
                                    const funcOnClickRow = onClickRow && (() => onClickRow(row, ir))

                                    return (
                                        <TableRow
                                            key={ir}
                                            sx={{ backgroundColor: lineColor }}
                                            onContextMenu={contextActions && (e => {
                                                e.preventDefault()
                                                setRowMenu({ mouseX: e.clientX + 2, mouseY: e.clientY - 6, row })
                                            })}
                                            onMouseDown={funcOnClickRow && (e => {
                                                if (e.button === 1) {
                                                    e.preventDefault()
                                                }
                                            })}
                                            onAuxClick={funcOnClickRow && (e => {
                                                if (e.button === 1) {
                                                    e.preventDefault()
                                                    funcOnClickRow()
                                                }
                                            })}
                                        >
                                            {leftActions.map((a, i) => {
                                                // displayed peut être une fonction (row) => bool : action conditionnelle par ligne.
                                                if (typeof a.displayed === 'function' && !a.displayed(row)) {
                                                    return <TableCell key={lineActions.indexOf(a)} variant='bodyIcon' sx={{ left: `${i * 23}px`, backgroundColor: lineColor }} />
                                                }
                                                const tooltip = typeof a.tooltip === 'function' ? a.tooltip(row) : a.tooltip
                                                const icon = (
                                                    <IconButton
                                                        sx={{ color: (typeof a.color === 'function' ? a.color(row) : a.color) ?? '#000' }}
                                                        size='actionTable'
                                                        onClick={() => a.onClick?.(row, ir)}
                                                    >
                                                        <Icon fontSize='inherit'>{typeof a.icon === 'function' ? a.icon(row) : a.icon}</Icon>
                                                    </IconButton>
                                                )
                                                return (
                                                    <TableCell
                                                        key={lineActions.indexOf(a)}
                                                        variant='bodyIcon'
                                                        align={ALIGN.CENTER}
                                                        sx={{ left: `${i * 23}px`, backgroundColor: lineColor }}
                                                    >
                                                        {tooltip ? (
                                                            <Tooltip key={i} title={tooltip} placement={a.positionTooltip}>
                                                                {icon}
                                                            </Tooltip>
                                                        ) : icon}
                                                    </TableCell>
                                                )
                                            })}
                                            {headers.map((header, i) => {
                                                const {
                                                    key,
                                                    width,
                                                    sticky,
                                                } = isObject(header) ? header : { key: header }

                                                if (!isObject(row[key]) || isValidElement(row[key])) {
                                                    return (
                                                        <TableCell
                                                            key={i}
                                                            variant='body'
                                                            sx={{
                                                                backgroundColor: lineColor,
                                                                cursor: isUndefined(funcOnClickRow) ? 'default' : 'pointer',

                                                                color: 'black',
                                                                maxWidth: width,
                                                                ...(!isNil(sticky) ? {
                                                                    position: 'sticky',
                                                                    [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                                                } : {}),
                                                            }}
                                                            onClick={funcOnClickRow}
                                                            padding={isNil(row[key]) ? 'none' : 'normal'}
                                                        >
                                                            {row[key]}
                                                        </TableCell>
                                                    )
                                                }
                                                const {
                                                    value,
                                                    tooltip,
                                                    align = ALIGN.LEFT,
                                                    verticalAlign,
                                                    color = lineColor,
                                                    fontColor = 'black', // getTextColorByHexColor(color)
                                                    onClick: onClickCell = funcOnClickRow,
                                                    noPadding,

                                                    rightIcon = {},
                                                    leftIcon = {},
                                                } = row[key] ?? {}

                                                const {
                                                    icon: leftIconName,
                                                    color: leftIconColor = 'grey',
                                                    onClick: leftIconOnClick,
                                                } = leftIcon

                                                const {
                                                    icon: rightIconName,
                                                    color: rightIconColor = 'grey',
                                                    onClick: rightIconOnClick,
                                                } = rightIcon

                                                return (
                                                    <TableCell
                                                        key={i}
                                                        variant='body'
                                                        sx={{
                                                            backgroundColor: color,
                                                            cursor: isUndefined(onClickCell) ? 'default' : 'pointer',

                                                            color: fontColor,
                                                            maxWidth: width,
                                                            ...(!isNil(sticky) ? {
                                                                position: 'sticky',
                                                                [sticky]: `${sticky === ALIGN.LEFT ? offsetLeftStickyHeaders[i] : offsetRightStickyHeaders[i + 1]}px`,
                                                            } : {}),
                                                            verticalAlign,
                                                        }}
                                                        onClick={onClickCell}
                                                        padding={noPadding || (isNil(value) && isNil(leftIconName) && isNil(rightIconName)) ? 'none' : 'normal'}
                                                    >
                                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: align, '& div:first-of-type': { width: '100%' } }}>
                                                            {!isUndefined(leftIconName) && (
                                                                <Icon sx={{ color: leftIconColor, marginRight: '5px' }} onClick={leftIconOnClick}>{leftIconName}</Icon>
                                                            )}
                                                            {tooltip ? <Tooltip title={tooltip}>{value ?? ''}</Tooltip> : (value ?? '')}
                                                            {!isUndefined(rightIconName) && (
                                                                <Icon sx={{ color: rightIconColor, marginLeft: '5px' }} onClick={rightIconOnClick}>{rightIconName}</Icon>
                                                            )}
                                                        </Box>
                                                    </TableCell>
                                                )
                                            })}
                                            {rightActions.map((a, i) => {
                                                // displayed peut être une fonction (row) => bool : action conditionnelle par ligne.
                                                if (typeof a.displayed === 'function' && !a.displayed(row)) {
                                                    return <TableCell key={lineActions.indexOf(a)} variant='bodyIcon' sx={{ left: `${i * 23}px`, backgroundColor: lineColor }} />
                                                }
                                                const tooltip = typeof a.tooltip === 'function' ? a.tooltip(row) : a.tooltip
                                                const icon = (
                                                    <IconButton
                                                        sx={{ color: (typeof a.color === 'function' ? a.color(row) : a.color) ?? '#000' }}
                                                        size='actionTable'
                                                        onClick={() => a.onClick?.(row, ir)}
                                                    >
                                                        <Icon fontSize='inherit'>{typeof a.icon === 'function' ? a.icon(row) : a.icon}</Icon>
                                                    </IconButton>
                                                )
                                                return (
                                                    <TableCell
                                                        key={lineActions.indexOf(a)}
                                                        variant='bodyIcon'
                                                        align={ALIGN.CENTER}
                                                        sx={{ left: `${i * 23}px`, backgroundColor: lineColor }}
                                                    >
                                                        {tooltip ? (
                                                            <Tooltip key={i} title={tooltip} placement={a.positionTooltip}>
                                                                {icon}
                                                            </Tooltip>
                                                        ) : icon}
                                                    </TableCell>
                                                )
                                            })}
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </>
                    )}
                </Table>
            </TableContainer>
            {!hideLines && columnFilteredRows.length > 10 && (
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50, 100, { value: -1, label: 'Tout' }]}
                    component='div'
                    count={columnFilteredRows.length}
                    rowsPerPage={rowsPerPage}
                    labelRowsPerPage='Lignes par page :'
                    page={safePage}
                    onPageChange={(_, p) => setPage(p)}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(+e.target.value)
                        setPage(0)
                    }}
                    showFirstButton
                    showLastButton
                />
            )}
            {
                status === STATUS.IDLE && (
                    <Grid container size={12} justifyContent='center' alignItems='center' spacing={1} sx={{ padding: '5 0' }}>
                        <SyncProblemIcon />
                        <Typography variant='h6' fontWeight='bold'>La donnée n'a pas été chargé</Typography>
                    </Grid>
                )
            }
            {
                status === STATUS.LOADING && (
                    <Grid container size={12} justifyContent='center' alignItems='center' spacing={1} sx={{ padding: '5 0' }}>
                        <CircularProgress color='#000' size={20} />
                        <Typography variant='h6' fontWeight='bold'>Chargement ...</Typography>
                    </Grid>
                )
            }
            {
                (isNil(status) || status === STATUS.SUCCEEDED) && columnFilteredRows.length === 0 && (
                    <Grid container size={12} justifyContent='center' alignItems='center' spacing={1} sx={{ padding: '5 0' }}>
                        <CloudOffIcon />
                        <Typography variant='h6' fontWeight='bold'>Aucune donnée à afficher</Typography>
                    </Grid>
                )
            }
            {!isNil(filterAnchor) && (
                <ColumnFilterPopover
                    anchorEl={filterAnchor.el}
                    columnLabel={filterAnchor.columnLabel}
                    options={activeColumnOptions}
                    selected={columnFilters[filterAnchor.columnKey] ?? []}
                    onChange={values => setColumnFilter(filterAnchor.columnKey, values)}
                    onClose={() => setFilterAnchor(null)}
                />
            )}
            {
                status === STATUS.REJECTED && (
                    <Grid container size={12} justifyContent='center' alignItems='center' spacing={1} sx={{ padding: '5 0' }}>
                        <ErrorOutlineIcon />
                        <Typography variant='h6' fontWeight='bold'>Error lors du chargement de la donnée</Typography>
                    </Grid>
                )
            }
            <Menu
                open={!isNil(rowMenu)}
                onClose={() => setRowMenu(null)}
                anchorReference='anchorPosition'
                anchorPosition={rowMenu ? { top: rowMenu.mouseY, left: rowMenu.mouseX } : undefined}
            >
                {rowMenuActions.filter(a => isNil(a.displayed) || a.displayed).map((a, index) => (
                    <MenuItem
                        key={index}
                        onClick={() => {
                            a.onClick(rowMenu.row)
                            setRowMenu(null)
                        }}
                    >
                        {a.icon && (isValidElement(a.icon)
                            ? <span style={{ marginRight: '5px', display: 'inline-flex' }}>{a.icon}</span>
                            : <Icon sx={{ marginRight: '5px', color: a.color }}>{a.icon}</Icon>)}
                        {a.label}
                    </MenuItem>
                ))}
            </Menu>
        </WrapperComponent>
    )
}

NewTable.propTypes = {
    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    // rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    //     PropTypes.number,
    //     PropTypes.string,
    //     PropTypes.element,
    //     PropTypes.shape({
    //         value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
    //         sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    //         onClick: PropTypes.func,
    //         color: PropTypes.string,
    //         fontColor: PropTypes.string,
    //         tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    //         align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT, ALIGN.CENTER]),
    //         noPadding: PropTypes.bool,
    //         leftIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //             onClick: PropTypes.func,
    //         }),
    //         rightIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //             onClick: PropTypes.func,
    //         }),
    //     }),
    // ]))).isRequired,
    headers: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
            sticky: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
            width: PropTypes.number,
            color: PropTypes.string,
            tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            filterable: PropTypes.bool,
        }),
    ])).isRequired,
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
        onClick: PropTypes.func.isRequired,
        align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
        displayed: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
        color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        positionTooltip: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end', 'left-start', 'left-end', 'right-start', 'right-end']),
    })),

    rowsPerPageDefault: PropTypes.oneOf([10, 25, 50, 100, -1]),
    onClickRow: PropTypes.func,
    defaultSort: PropTypes.shape({
        column: PropTypes.string.isRequired, // header
        direction: PropTypes.oneOf([SORT.ASC, SORT.DESC]).isRequired,
    }),

    status: PropTypes.oneOf([STATUS.IDLE, STATUS.LOADING, STATUS.SUCCEEDED, STATUS.REJECTED]),
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    WrapperComponent: PropTypes.elementType,
    hideLines: PropTypes.bool,
    filterable: PropTypes.bool,
    contextActions: PropTypes.func,
    rememberState: PropTypes.bool,
    rememberAll: PropTypes.bool,
}

const CardTable = ({
    title = '',
    subTitle = '',
    subtitleSize,
    actions = [],
    hideNbElements = false,
    color = '#cce6ff',

    rows = [],
    headers = [],
    lineActions = [],

    rowsPerPageDefault,
    onClickRow,
    defaultSort,

    status,
    maxHeight,
    hideLines,
    filterable = true,
    contextActions,
    rememberState = false,
    rememberAll = false,
}) => {
    const formattedTitle = useMemo(() => {
        if (hideNbElements) {
            return title
        }
        const nbElements = rows.length
        return (
            <>
                {isValidElement(title) ? title : <span style={{ fontSize: '1.3rem', fontWeight: 'bold' }}>{title}</span>}
                <span style={{ fontSize: '1.3rem', paddingLeft: '5px' }}>({nbElements} {nbElements > 1 ? 'Eléments' : 'Elément'})</span>
            </>
        )
    }, [hideNbElements, rows.length, title])

    return (
        <Card>
            <CardTitle
                title={formattedTitle}
                subTitle={subTitle}
                actions={actions}
                color={color}
                subtitleSize={subtitleSize}
            />
            <NewTable
                rows={rows}
                headers={headers}
                lineActions={lineActions}

                onClickRow={onClickRow}
                defaultSort={defaultSort}

                status={status}
                maxHeight={maxHeight}
                WrapperComponent={WrapperCardContent}
                rowsPerPageDefault={rowsPerPageDefault}
                hideLines={hideLines}
                filterable={filterable}
                contextActions={contextActions}
                rememberState={rememberState}
                rememberAll={rememberAll}
            />
        </Card>
    )
}

CardTable.propTypes = {
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    subTitle: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    subtitleSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    actions: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.element,
        PropTypes.shape({
            icon: PropTypes.string,
            tooltip: PropTypes.string,
            onClick: PropTypes.func,
            color: PropTypes.string,
            displayed: PropTypes.bool,
        }),
    ])),
    rowsPerPageDefault: PropTypes.oneOf([10, 25, 50, 100, -1]),
    hideNbElements: PropTypes.bool,
    rememberAll: PropTypes.bool,
    color: PropTypes.string,

    rows: PropTypes.arrayOf(PropTypes.object).isRequired,
    // rows: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.oneOfType([
    //     PropTypes.number,
    //     PropTypes.string,
    //     PropTypes.element,
    //     PropTypes.shape({
    //         value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.element]),
    //         sortValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    //         onClick: PropTypes.func,
    //         color: PropTypes.string,
    //         fontColor: PropTypes.string,
    //         tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
    //         align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT, ALIGN.CENTER]),
    //         noPadding: PropTypes.bool,
    //         leftIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //             onClick: PropTypes.func,
    //         }),
    //         rightIcon: PropTypes.shape({
    //             icon: PropTypes.string,
    //             color: PropTypes.string,
    //             onClick: PropTypes.func,
    //         }),
    //     }),
    // ]))).isRequired,
    headers: PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
            key: PropTypes.string.isRequired,
            value: PropTypes.oneOfType([
                PropTypes.string,
                PropTypes.element,
            ]),
            sticky: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
            width: PropTypes.number,
            color: PropTypes.string,
            tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
            filterable: PropTypes.bool,
        }),
    ])).isRequired,
    lineActions: PropTypes.arrayOf(PropTypes.shape({
        icon: PropTypes.oneOfType([PropTypes.string, PropTypes.func]).isRequired,
        onClick: PropTypes.func.isRequired,
        align: PropTypes.oneOf([ALIGN.LEFT, ALIGN.RIGHT]),
        displayed: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
        color: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
        tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.func]),
        positionTooltip: PropTypes.oneOf(['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end', 'left-start', 'left-end', 'right-start', 'right-end']),
    })),

    onClickRow: PropTypes.func,
    defaultSort: PropTypes.shape({
        column: PropTypes.string.isRequired, // header
        direction: PropTypes.oneOf([SORT.ASC, SORT.DESC]).isRequired,
    }),

    status: PropTypes.oneOf([STATUS.IDLE, STATUS.LOADING, STATUS.SUCCEEDED, STATUS.REJECTED]),
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    hideLines: PropTypes.bool,
    filterable: PropTypes.bool,
    contextActions: PropTypes.func,
    rememberState: PropTypes.bool,
}

export {
    NewTable,
    CardTable,
    WrapperBox,
    WrapperPaper,
    WrapperCardContent,
    WrapperAccordionDetails,
    sortRows,
    SORT,
    ALIGN,
}